import { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { uploadFile, type UploadResult } from "../devAgentService";
import { ACCEPTED_FILE_EXTS, ACCEPTED_FILE_LABEL } from "./constants";

export function useAgentFeedUpload() {
  const [uploadedFiles, setUploadedFiles] = useState<UploadResult[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const accepted: File[] = [];
      const rejected: string[] = [];
      for (const file of Array.from(files)) {
        const dot = file.name.lastIndexOf(".");
        const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : "";
        if ((ACCEPTED_FILE_EXTS as readonly string[]).includes(ext)) {
          accepted.push(file);
        } else {
          rejected.push(file.name);
        }
      }

      if (rejected.length > 0) {
        toast.error(
          `Unsupported file type: ${rejected.join(", ")}. Allowed: ${ACCEPTED_FILE_LABEL}`,
        );
      }
      if (accepted.length === 0) {
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      setUploading(true);
      try {
        for (const file of accepted) {
          const result = await uploadFile(file);
          setUploadedFiles((prev) => [...prev, result]);
        }
      } catch (error: unknown) {
        const axiosError = error as {
          response?: { status?: number; data?: { detail?: string; message?: string } };
          message?: string;
        };
        const detail =
          axiosError?.response?.data?.detail ||
          axiosError?.response?.data?.message;
        const status = axiosError?.response?.status;
        const message = detail
          ? detail
          : status
            ? `Upload failed (${status}). Check the file type — allowed: ${ACCEPTED_FILE_LABEL}`
            : axiosError?.message || "Upload failed";
        toast.error(message);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [],
  );

  return {
    uploadedFiles,
    setUploadedFiles,
    uploading,
    fileInputRef,
    handleFileUpload,
  };
}
